import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import {
  Cog6ToothIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { TWclassNames } from "../../utils/Div";
import { PromangRichTextEditor } from "../../utils/PromangRichTextEditor";
import OfferCoverPage from "./offer-cover-page";
import "./offer-workspace.css";
import {
  DOCUMENT_BLOCK_TYPES,
  HEADING_LEVELS,
  OFFER_EDIT_SOURCED_BLOCK_TYPES,
  TEXT_EDITABLE_BLOCK_TYPES,
  VISIBILITY_VALUES,
} from "./offer-document-structure-defaults";

const ACCENT = "#1463C7";
const ACTIVE_BG = "#EAF3FD";
const SPACING_SIDES = ["top", "right", "bottom", "left"];

/**
 * Wide right-side inspector drawer for a single document_structure block —
 * replaces the old 230px BlockEditorPanel strip. Preview stays visible to
 * the left (scrim over it, drawer doesn't cover the structure list).
 */
export default function OfferDocumentBlockEditor({
  open,
  block,
  offer,
  onClose,
  onPatch,
  onCoverAutoSave,
}) {
  const { t } = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const isOfferEditSourced = block
    ? OFFER_EDIT_SOURCED_BLOCK_TYPES.has(block.type)
    : false;
  const isTextEditable = block ? TEXT_EDITABLE_BLOCK_TYPES.has(block.type) : false;

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setAdvancedOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, block?.id]);

  if (typeof document === "undefined") return null;

  const typeLabel = block
    ? t(`offers.documentBuilder.blocks.${block.type}`, { defaultValue: block.type })
    : "";

  return createPortal(
    <>
      <div
        className={TWclassNames(
          "fixed inset-0 z-40 bg-slate-900/40 transition-opacity duration-300",
          open ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className={TWclassNames(
          "fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-2xl transition-transform duration-300 ease-out sm:w-[min(820px,100vw)]",
          open ? "translate-x-0" : "translate-x-full"
        )}
        role="dialog"
        aria-modal="true"
        aria-label={t("offers.documentBuilder.editor.inspector_title")}
      >
        {open && block ? (
          <>
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="flex min-w-0 items-start gap-3">
                <span
                  className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: ACTIVE_BG, color: ACCENT }}
                >
                  <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {block.title || typeLabel}
                  </p>
                  <p className="text-xs text-slate-500">
                    {t("offers.documentBuilder.editor.inspector_subtitle", {
                      type: typeLabel,
                    })}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label={t("Close")}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
              <BlockEditorBody
                block={block}
                offer={offer}
                onPatch={onPatch}
                onCoverAutoSave={onCoverAutoSave}
                isOfferEditSourced={isOfferEditSourced}
                isTextEditable={isTextEditable}
                advancedOpen={advancedOpen}
                setAdvancedOpen={setAdvancedOpen}
              />
            </div>
          </>
        ) : null}
      </div>
    </>,
    document.body
  );
}

function BlockEditorBody({
  block,
  offer,
  onPatch,
  onCoverAutoSave,
  isOfferEditSourced,
  isTextEditable,
  advancedOpen,
  setAdvancedOpen,
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Title is plain text — used as section heading in preview/PDF, not rich content. */}
      <section className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("offers.documentBuilder.editor.title_label")}
          </span>
          <input
            type="text"
            value={block.title || ""}
            onChange={(e) => onPatch(block.id, { title: e.target.value }, { debounceMs: 1200 })}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#1463C7] focus:ring-1 focus:ring-[#1463C7]"
            placeholder={t("offers.documentBuilder.editor.title_placeholder")}
          />
        </label>
      </section>

      <SettingsSection block={block} onPatch={onPatch} />

      <TypeSpecificSection block={block} onPatch={onPatch} offer={offer} onCoverAutoSave={onCoverAutoSave} />

      {isOfferEditSourced ? (
        <section className="space-y-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("offers.documentBuilder.editor.body_label")}
          </span>
          <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
            {t("offers.documentBuilder.editor.offer_edit_sourced_hint")}
          </p>
        </section>
      ) : isTextEditable ? (
        <section className="space-y-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("offers.documentBuilder.editor.body_label")}
          </span>
          {block.type === DOCUMENT_BLOCK_TYPES.ALTERNATIVES ? (
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {t("offers.documentBuilder.editor.alternatives_hint")}
            </p>
          ) : null}
          <PromangRichTextEditor
            key={`doc-body-${block.id}`}
            value={block.content?.body || ""}
            onChange={(html) =>
              onPatch(block.id, { content: { body: html } }, { debounceMs: 1200 })
            }
            placeholder={t("offers.documentBuilder.editor.body_placeholder")}
            size="lg"
            framed
            templateButtonLabel={t("offers.documentBuilder.editor.load_template")}
          />
        </section>
      ) : block.type === DOCUMENT_BLOCK_TYPES.ATTACHMENTS ? (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
          {t("offers.documentBuilder.editor.attachments_hint")}
        </p>
      ) : null}

      <AdvancedLayoutSection
        block={block}
        onPatch={onPatch}
        open={advancedOpen}
        onToggle={() => setAdvancedOpen((v) => !v)}
      />
    </div>
  );
}

function SettingsSection({ block, onPatch }) {
  const { t } = useTranslation();

  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("offers.documentBuilder.editor.settings_title")}
      </p>

      <ToggleRow
        label={t("offers.documentBuilder.editor.enabled_label")}
        hint={t("offers.documentBuilder.editor.enabled_hint")}
        checked={block.enabled !== false}
        onChange={(checked) => onPatch(block.id, { enabled: checked })}
      />
      <ToggleRow
        label={t("offers.documentBuilder.editor.show_in_pdf_label")}
        hint={t("offers.documentBuilder.editor.show_in_pdf_hint")}
        checked={block.show_in_pdf !== false}
        onChange={(checked) => onPatch(block.id, { show_in_pdf: checked })}
      />
      <ToggleRow
        label={t("offers.documentBuilder.editor.page_break_label")}
        hint={t("offers.documentBuilder.editor.page_break_hint")}
        checked={block.page_break_before === true}
        onChange={(checked) => onPatch(block.id, { page_break_before: checked })}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            {t("offers.documentBuilder.editor.heading_level_label")}
          </span>
          <select
            value={block.heading_level ?? ""}
            onChange={(e) =>
              onPatch(block.id, {
                heading_level: e.target.value === "" ? null : Number(e.target.value),
              })
            }
            className="offer-select"
          >
            <option value="">{t("offers.documentBuilder.editor.heading_level_none")}</option>
            {HEADING_LEVELS.map((level) => (
              <option key={level} value={level}>
                H{level}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            {t("offers.documentBuilder.editor.visibility_label")}
          </span>
          <select
            value={block.content?.visibility || "all"}
            onChange={(e) =>
              onPatch(block.id, { content: { visibility: e.target.value } })
            }
            className="offer-select"
          >
            {VISIBILITY_VALUES.map((value) => (
              <option key={value} value={value}>
                {t(`offers.documentBuilder.editor.visibility_${value}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </section>
  );
}

function TypeSpecificSection({ block, onPatch, offer, onCoverAutoSave }) {
  const { t } = useTranslation();

  if (block.type === DOCUMENT_BLOCK_TYPES.COVER) {
    return (
      <section className="space-y-4">
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("offers.documentBuilder.editor.cover.settings_title")}
          </p>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              {t("offers.documentBuilder.editor.cover.subtitle_label")}
            </span>
            <input
              type="text"
              value={block.content?.subtitle || ""}
              onChange={(e) =>
                onPatch(block.id, { content: { subtitle: e.target.value } }, { debounceMs: 1200 })
              }
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#1463C7] focus:ring-1 focus:ring-[#1463C7]"
              placeholder={t("offers.documentBuilder.editor.cover.subtitle_placeholder")}
            />
          </label>
          <ToggleRow
            label={t("offers.documentBuilder.editor.cover.show_customer_label")}
            checked={block.content?.show_customer !== false}
            onChange={(checked) => onPatch(block.id, { content: { show_customer: checked } })}
          />
          <ToggleRow
            label={t("offers.documentBuilder.editor.cover.show_offer_date_label")}
            checked={block.content?.show_offer_date !== false}
            onChange={(checked) => onPatch(block.id, { content: { show_offer_date: checked } })}
          />
          <p className="text-xs text-slate-500">
            {t("offers.documentBuilder.editor.cover.image_hint")}
          </p>
        </div>

        <div className="max-h-[520px] overflow-y-auto rounded-xl border border-slate-200">
          <Formik
            enableReinitialize
            initialValues={{
              id: offer.id,
              cover_page_active: offer.cover_page_active || false,
              cover_page_title: offer.cover_page_title || "",
              cover_page_description: offer.cover_page_description || "",
              cover_page_image_id: offer.cover_page_image_id || null,
            }}
            onSubmit={() => {}}
          >
            {(formik) => (
              <OfferCoverPage
                offer={offer}
                formik={formik}
                onAutoSave={() => {
                  onCoverAutoSave(formik.values);
                  // Keep block-level image_id mirrored to the offer cover image
                  // (no separate uploader in this drawer — see docs residual risk).
                  if (formik.values.cover_page_image_id !== block.content?.image_id) {
                    onPatch(block.id, {
                      content: { image_id: formik.values.cover_page_image_id },
                    });
                  }
                }}
              />
            )}
          </Formik>
        </div>
      </section>
    );
  }

  if (block.type === DOCUMENT_BLOCK_TYPES.LETTER) {
    return (
      <section className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2">
        <p className="col-span-full text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("offers.documentBuilder.editor.letter.settings_title")}
        </p>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            {t("offers.documentBuilder.editor.letter.greeting_label")}
          </span>
          <input
            type="text"
            value={block.content?.greeting || ""}
            onChange={(e) =>
              onPatch(block.id, { content: { greeting: e.target.value } }, { debounceMs: 1200 })
            }
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#1463C7] focus:ring-1 focus:ring-[#1463C7]"
            placeholder={t("offers.documentBuilder.editor.letter.greeting_placeholder")}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            {t("offers.documentBuilder.editor.letter.signoff_label")}
          </span>
          <input
            type="text"
            value={block.content?.signoff || ""}
            onChange={(e) =>
              onPatch(block.id, { content: { signoff: e.target.value } }, { debounceMs: 1200 })
            }
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#1463C7] focus:ring-1 focus:ring-[#1463C7]"
            placeholder={t("offers.documentBuilder.editor.letter.signoff_placeholder")}
          />
        </label>
      </section>
    );
  }

  if (block.type === DOCUMENT_BLOCK_TYPES.PRICE) {
    return (
      <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("offers.documentBuilder.editor.price.settings_title")}
        </p>
        <p className="text-sm text-slate-600">{t("offers.documentBuilder.editor.price_hint")}</p>
        <ToggleRow
          label={t("offers.documentBuilder.editor.price.show_vat_label")}
          checked={block.content?.show_vat !== false}
          onChange={(checked) => onPatch(block.id, { content: { show_vat: checked } })}
        />
        <ToggleRow
          label={t("offers.documentBuilder.editor.price.show_summary_table_label")}
          checked={block.content?.show_summary_table === true}
          onChange={(checked) => onPatch(block.id, { content: { show_summary_table: checked } })}
        />
        <div className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            {t("offers.documentBuilder.editor.price.note_label")}
          </span>
          <PromangRichTextEditor
            key={`doc-price-note-${block.id}`}
            value={block.content?.note || ""}
            onChange={(html) =>
              onPatch(block.id, { content: { note: html } }, { debounceMs: 1200 })
            }
            placeholder={t("offers.documentBuilder.editor.price.note_placeholder")}
            size="sm"
            framed
            templatesEnabled={false}
            tablesEnabled={false}
          />
        </div>
      </section>
    );
  }

  if (block.type === DOCUMENT_BLOCK_TYPES.SIGNATURE) {
    const signatoryCount = Number(block.content?.signatory_count) || 1;
    return (
      <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("offers.documentBuilder.editor.signature.settings_title")}
        </p>
        <ToggleRow
          label={t("offers.documentBuilder.editor.signature.show_date_field_label")}
          checked={block.content?.show_date_field !== false}
          onChange={(checked) => onPatch(block.id, { content: { show_date_field: checked } })}
        />
        <ToggleRow
          label={t("offers.documentBuilder.editor.signature.show_title_field_label")}
          checked={block.content?.show_title_field !== false}
          onChange={(checked) => onPatch(block.id, { content: { show_title_field: checked } })}
        />
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-700">
            {t("offers.documentBuilder.editor.signature.signatory_count_label")}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={signatoryCount <= 1}
              onClick={() =>
                onPatch(block.id, { content: { signatory_count: Math.max(1, signatoryCount - 1) } })
              }
              className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-600 transition-colors hover:border-[#1463C7] hover:text-[#1463C7] disabled:opacity-40"
              aria-label="-"
            >
              −
            </button>
            <span className="w-6 text-center text-sm font-semibold tabular-nums text-slate-900">
              {signatoryCount}
            </span>
            <button
              type="button"
              disabled={signatoryCount >= 4}
              onClick={() =>
                onPatch(block.id, { content: { signatory_count: Math.min(4, signatoryCount + 1) } })
              }
              className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-600 transition-colors hover:border-[#1463C7] hover:text-[#1463C7] disabled:opacity-40"
              aria-label="+"
            >
              +
            </button>
          </div>
        </div>
      </section>
    );
  }

  return null;
}

function AdvancedLayoutSection({ block, onPatch, open, onToggle }) {
  const { t } = useTranslation();
  const padding = block.content?.padding || {};
  const margin = block.content?.margin || {};

  const updateSpacing = (key, side, value) => {
    const current = block.content?.[key] || {};
    const next = { ...current };
    if (value === "") {
      delete next[side];
    } else {
      next[side] = Number(value);
    }
    onPatch(block.id, { content: { [key]: Object.keys(next).length ? next : null } });
  };

  return (
    <section className="rounded-xl border border-slate-200">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("offers.documentBuilder.editor.advanced_layout")}
        </span>
        {open ? (
          <ChevronDownIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
        )}
      </button>
      {open ? (
        <div className="space-y-4 border-t border-slate-100 px-4 py-4">
          <SpacingGrid
            label={t("offers.documentBuilder.editor.padding_label")}
            values={padding}
            onChange={(side, value) => updateSpacing("padding", side, value)}
          />
          <SpacingGrid
            label={t("offers.documentBuilder.editor.margin_label")}
            values={margin}
            onChange={(side, value) => updateSpacing("margin", side, value)}
          />
        </div>
      ) : null}
    </section>
  );
}

function SpacingGrid({ label, values, onChange }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="mb-2 text-xs font-medium text-slate-600">{label}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {SPACING_SIDES.map((side) => (
          <label key={side} className="block">
            <span className="mb-1 block text-[11px] text-slate-400">
              {t(`offers.documentBuilder.editor.side_${side}`)}
            </span>
            <input
              type="number"
              value={values[side] ?? ""}
              onChange={(e) => onChange(side, e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-[#1463C7] focus:ring-1 focus:ring-[#1463C7]"
              placeholder="0"
            />
          </label>
        ))}
      </div>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <div className="min-w-0">
        <p className="text-sm text-slate-700">{label}</p>
        {hint ? <p className="text-xs text-slate-400">{hint}</p> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={TWclassNames(
          "relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors",
          checked ? "bg-[#1463C7]" : "bg-slate-300"
        )}
      >
        <span
          className={TWclassNames(
            "inline-block transform rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-[22px]" : "translate-x-[3px]"
          )}
          style={{ height: 18, width: 18 }}
        />
      </button>
    </div>
  );
}
